import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { apiClient } from '../services/api';
import toast from 'react-hot-toast';
import { getImageUrl, hideBrokenImage } from '../utils/imageUrl';

const BlogList = () => {
  const { user } = useAuth();
  const [blogs, setBlogs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);

  const blogsPerPage = 10;

  const fetchBlogs = useCallback(async () => {
    try {
      setLoading(true);
      let url = `/blogs?page=${currentPage}&limit=${blogsPerPage}&status=published`;
      
      if (selectedCategory) {
        url = `/categories/${selectedCategory}/blogs?page=${currentPage}&limit=${blogsPerPage}`;
      }

      const response = await apiClient.get(url);
      setBlogs(response.data.blogs || response.data.data?.blogs || []);
      setTotalPages(response.data.pagination?.pages || 1);
    } catch (error) {
      toast.error('Failed to fetch blogs');
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [currentPage, selectedCategory]);

  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiClient.get('/categories');
      setCategories(response.data.categories || response.data.data?.categories || []);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
    }
  }, []);

  useEffect(() => {
    fetchBlogs();
    fetchCategories();
  }, [fetchBlogs, fetchCategories]);

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      try {
        setLoading(true);
        const response = await apiClient.get(`/search/blogs?q=${searchQuery}`);
        setBlogs(response.data.blogs || response.data.data?.blogs || []);
        setTotalPages(1);
      } catch (error) {
        toast.error('Search failed');
      } finally {
        setLoading(false);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header Navigation */}
      <header className="bg-white shadow-sm border-b sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <img 
                src="/fbrlogo1.svg" 
                alt="FBR Logo" 
                className="h-10 w-10 mr-3"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
              <Link to="/" className="text-2xl font-bold text-blue-600 hover:text-blue-700">
                FBRSTAPP
              </Link>
            </div>
            <nav className="flex items-center space-x-6">
              <Link to="/" className="text-gray-600 hover:text-gray-900 font-medium">Home</Link>
              <Link to="/blog" className="text-gray-600 hover:text-gray-900 font-medium">Blog</Link>
              {user ? (
                <Link
                  to="/app/dashboard"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                >
                  Dashboard
                </Link>
              ) : (
                <>
                  <Link
                    to="/login"
                    className="text-blue-600 hover:text-blue-700 font-medium"
                  >
                    Sign In
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium"
                  >
                    Get Started
                  </Link>
                </>
              )}
            </nav>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white py-14 px-4 shadow-inner">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl font-extrabold mb-4 tracking-tight">FBR Invoicing &amp; Tax Insights</h1>
          <p className="text-lg sm:text-xl text-blue-100 max-w-2xl mx-auto">Latest updates, tutorials, SRO compliance guides, and digital invoicing strategies in Pakistan</p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-12">
        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-10">
          <div className="flex gap-2 max-w-2xl mx-auto">
            <input
              type="text"
              placeholder="Search blog posts..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-600 shadow-sm"
            />
            <button
              type="submit"
              className="bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 font-semibold shadow-md transition"
            >
              Search
            </button>
          </div>
        </form>

        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="md:col-span-1">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 sticky top-24">
              <h3 className="text-lg font-bold text-gray-900 mb-4 pb-2 border-b border-gray-100">Categories</h3>
              <div className="space-y-1">
                <button
                  onClick={() => {
                    setSelectedCategory(null);
                    setCurrentPage(1);
                  }}
                  className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                    selectedCategory === null ? 'bg-blue-50 text-blue-600 font-bold' : 'text-gray-600 hover:bg-gray-50'
                  }`}
                >
                  All Posts
                </button>
                {categories.map(cat => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setSelectedCategory(cat.slug);
                      setCurrentPage(1);
                    }}
                    className={`block w-full text-left px-3 py-2 rounded-lg text-sm transition ${
                      selectedCategory === cat.slug ? 'bg-blue-50 text-blue-600 font-bold' : 'text-gray-600 hover:bg-gray-50'
                    }`}
                  >
                    {cat.name} <span className="text-xs text-gray-400">({cat.post_count})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="md:col-span-3">
            {loading ? (
              <div className="text-center py-16 text-gray-500 font-medium">Loading blog articles...</div>
            ) : blogs.length > 0 ? (
              <>
                <div className="space-y-8">
                  {blogs.map(blog => {
                    const imageUrl = getImageUrl(blog.featured_image);
                    return (
                    <article key={blog.id} className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300 border border-gray-100 flex flex-col md:flex-row">
                      <div className="md:w-2/5 flex-shrink-0 bg-gray-100 overflow-hidden relative min-h-[220px] md:min-h-[260px]">
                        <Link to={`/blog/${blog.slug}`} className="block w-full h-full">
                          {imageUrl ? (
                            <img
                              src={imageUrl}
                              alt={blog.title}
                              className="w-full h-full object-cover hover:scale-105 transition-transform duration-500 min-h-[220px]"
                              onError={hideBrokenImage}
                            />
                          ) : (
                            <div className="w-full h-full min-h-[220px] bg-gradient-to-br from-blue-600 to-indigo-700 flex items-center justify-center p-6 text-white text-center font-bold text-lg">
                              FBRSTAPP
                            </div>
                          )}
                        </Link>
                      </div>
                      <div className="p-6 md:p-8 flex-1 flex flex-col justify-between">
                        <div>
                          {blog.category_name && (
                            <Link
                              to={`/blog/category/${blog.category_slug}`}
                              className="inline-block px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-xs font-semibold uppercase tracking-wider mb-3 hover:bg-blue-100 transition"
                            >
                              {blog.category_name}
                            </Link>
                          )}
                          <Link
                            to={`/blog/${blog.slug}`}
                            className="block text-xl md:text-2xl font-bold text-gray-900 hover:text-blue-600 transition leading-snug mb-3"
                          >
                            {blog.title}
                          </Link>
                          <div className="flex flex-wrap items-center gap-3 text-gray-500 text-xs sm:text-sm mb-4">
                            <span>By {blog.author_name}</span>
                            <span>•</span>
                            <span>{new Date(blog.publish_date).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                            <span>•</span>
                            <span>{blog.views} views</span>
                          </div>
                          <p className="text-gray-600 text-sm leading-relaxed line-clamp-3 mb-4">
                            {blog.excerpt || (blog.content ? blog.content.replace(/<[^>]*>?/gm, '').substring(0, 180) : '')}...
                          </p>
                        </div>
                        <div>
                          <Link
                            to={`/blog/${blog.slug}`}
                            className="inline-flex items-center text-blue-600 hover:text-blue-800 font-bold text-sm group"
                          >
                            Read Full Article <span className="ml-1 group-hover:translate-x-1 transition-transform">→</span>
                          </Link>
                        </div>
                      </div>
                    </article>
                    );
                  })}
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex justify-center gap-2 mt-12">
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map(page => (
                      <button
                        key={page}
                        onClick={() => setCurrentPage(page)}
                        className={`px-4 py-2 rounded-lg font-medium transition ${
                          currentPage === page
                            ? 'bg-blue-600 text-white shadow-md'
                            : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
                        }`}
                      >
                        {page}
                      </button>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-16 bg-white rounded-2xl border border-gray-200 text-gray-500">
                No blog posts found.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogList;
